import React from 'react';
import { Icon } from '@iconify/react';
import { LoyaltyProgram } from '@/types/loyalty';

interface Props {
  formData: LoyaltyProgram;
  onChange: (patch: Partial<LoyaltyProgram>) => void;
  disabled?: boolean;
}

const formatThousandWithZero = (val: number | undefined | null): string => {
  if (val === undefined || val === null || isNaN(val)) return '0';
  return Math.round(val).toLocaleString('id-ID');
};

const parseDigits = (raw: string): number => {
  const digits = raw.replace(/\D/g, '');
  return digits ? Number(digits) : 0;
};

export const MembershipSettingsForm: React.FC<Props> = ({
  formData,
  onChange,
  disabled = false,
}) => {
  const durationMonths = formData.membership_duration_months || 12;
  const priorityDays = formData.membership_priority_days || 7;

  return (
    <div className="space-y-5">
      {/* Row 1: Biaya pendaftaran & Masa aktif */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div>
            <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Biaya pendaftaran
            </label>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Isi 0 jika gratis
            </p>
          </div>
          <div className="flex items-center h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden focus-within:border-[#FC480D] transition-colors">
            <span className="h-full px-3.5 bg-[#F8F9FA] border-r border-[#E0E6ED] flex items-center text-xs font-medium text-[#8C8C8C] select-none">
              Rp
            </span>
            <input
              type="text"
              inputMode="numeric"
              disabled={disabled}
              value={formatThousandWithZero(formData.membership_registration_fee)}
              onChange={(e) =>
                onChange({ membership_registration_fee: parseDigits(e.target.value) })
              }
              placeholder="50.000"
              className="w-full h-full px-3.5 text-xs sm:text-[13px] font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none disabled:bg-[#F8F9FA]"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <div>
            <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Masa aktif
            </label>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Member perlu perpanjang setelahnya
            </p>
          </div>
          <div className="flex items-center justify-between h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden">
            <button
              type="button"
              disabled={disabled || durationMonths <= 1}
              onClick={() =>
                onChange({ membership_duration_months: Math.max(1, durationMonths - 1) })
              }
              className="w-10 h-full border-r border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
            >
              <Icon icon="ph:minus" className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              {durationMonths} bulan
            </span>
            <button
              type="button"
              disabled={disabled || durationMonths >= 24}
              onClick={() =>
                onChange({ membership_duration_months: Math.min(24, durationMonths + 1) })
              }
              className="w-10 h-full border-l border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
            >
              <Icon icon="ph:plus" className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Row 2: Diskon booking & Diskon sewa & produk */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div>
            <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Diskon booking
            </label>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Untuk semua lapangan
            </p>
          </div>
          <div className="flex items-center h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden focus-within:border-[#FC480D] transition-colors">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              disabled={disabled}
              value={formData.membership_booking_discount ?? 0}
              onChange={(e) =>
                onChange({
                  membership_booking_discount: Math.min(
                    100,
                    Math.max(0, Number(e.target.value))
                  ),
                })
              }
              placeholder="10"
              className="w-full h-full px-3.5 text-xs sm:text-[13px] font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none disabled:bg-[#F8F9FA] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="h-full px-3.5 bg-[#F8F9FA] border-l border-[#E0E6ED] flex items-center text-xs font-medium text-[#8C8C8C] select-none">
              %
            </span>
          </div>
        </div>

        <div className="space-y-1.5">
          <div>
            <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Diskon sewa & produk
            </label>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Isi 0 jika tidak ada
            </p>
          </div>
          <div className="flex items-center h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden focus-within:border-[#FC480D] transition-colors">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              disabled={disabled}
              value={formData.membership_product_discount ?? 0}
              onChange={(e) =>
                onChange({
                  membership_product_discount: Math.min(
                    100,
                    Math.max(0, Number(e.target.value))
                  ),
                })
              }
              placeholder="5"
              className="w-full h-full px-3.5 text-xs sm:text-[13px] font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none disabled:bg-[#F8F9FA] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="h-full px-3.5 bg-[#F8F9FA] border-l border-[#E0E6ED] flex items-center text-xs font-medium text-[#8C8C8C] select-none">
              %
            </span>
          </div>
        </div>
      </div>

      {/* Divider + Prioritas booking */}
      <div className="border-t border-[#F0F2F5] pt-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <span className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Prioritas booking
            </span>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Member bisa booking lebih awal dari non-member
            </p>
          </div>
          <label
            className={`relative inline-flex items-center ${
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            }`}
          >
            <input
              type="checkbox"
              disabled={disabled}
              checked={formData.membership_priority_booking}
              onChange={(e) =>
                onChange({ membership_priority_booking: e.target.checked })
              }
              className="sr-only peer"
            />
            <div className="w-10 h-5 bg-[#E0E6ED] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FC480D]" />
          </label>
        </div>

        {formData.membership_priority_booking && (
          <div className="space-y-1.5">
            <div>
              <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
                Buka jadwal lebih awal
              </label>
              <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
                Dibanding pelanggan biasa
              </p>
            </div>

            <div className="flex items-center justify-between h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden">
              <button
                type="button"
                disabled={disabled || priorityDays <= 1}
                onClick={() =>
                  onChange({ membership_priority_days: Math.max(1, priorityDays - 1) })
                }
                className="w-10 h-full border-r border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
              >
                <Icon icon="ph:minus" className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs sm:text-[13px] font-semibold text-[#0E1726]">
                {priorityDays} hari
              </span>
              <button
                type="button"
                disabled={disabled || priorityDays >= 30}
                onClick={() =>
                  onChange({ membership_priority_days: Math.min(30, priorityDays + 1) })
                }
                className="w-10 h-full border-l border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
              >
                <Icon icon="ph:plus" className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembershipSettingsForm;
